import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { ReactComponent as Sentence } from "../../../assets/icons/sentence.svg";
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { updateDashboardFunc } from '../../../lib/helper';
import theme from "../../../theme";
import { CustomField as CustomFieldType } from "../../../types/common";
import CustomField from "../../atoms/CustomField";
import { Dashboard } from "../../../types/files";

const useStyles = makeStyles(() => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 'auto',
            maxWidth: '100%',
            minWidth: 500,
            height: 'fit-content',
            minHeight: '30vh',
            maxHeight: '70vh',
            padding: '16px 24px 8px 24px',
            boxShadow: '0px 20px 30px -5px rgba(16, 24, 40, 0.06)',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.4rem',
        gap: 16,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '8px 0',
        height: '100%',
        overflow: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 28,
        height: 28,
    },
    content: {
        padding: '24px',
        width: '100%',
        maxHeight: '500px',
        overflow: 'auto',
    },
    cancelBtn: {
        width: 100,
        height: 40,
        borderRadius: 40,
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    saveBtn: {
        width: 100,
        height: 40,
        borderRadius: 40,
        backgroundColor: theme.colors.primary['600'],
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}))

const ManageCustomFieldsModal: FC<{
    open: boolean;
    filterType?: string;
    onClose: () => void;
}> = ({ open, filterType, onClose }) => {
    const classes = useStyles();
    const { settingsLoaded, customFields, setCustomFields } = useContext(GroupSettingsContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);
    const [customField, setCustomField] = useState<CustomFieldType | undefined>(undefined);

    const handleUpdateFieldValue = useCallback((title: string, context: string, create?: boolean) => {
        if (!create) {
            const lookUpCustomField = customFields.find(({ name }) => name.toLowerCase() === title.toLowerCase());

            if (!lookUpCustomField) {
                setCustomFields(prev => [...prev, ({ name: title, additionalContextQuery: context } as CustomFieldType)]
                    .map(({ name, additionalContextQuery }) => ({name, additionalContextQuery})));
            }  else {
                setCustomFields(prev => (prev.map(custom => custom.name.toLowerCase() === title.toLowerCase()
                    ? ({ ...custom, additionalContextQuery: context }) : custom) as CustomFieldType[])
                        .map(({ name, additionalContextQuery }) => ({name, additionalContextQuery})));
            }
        }
        setCustomField({ name: title, additionalContextQuery: context } as CustomFieldType);
    // eslint-disable-next-line
    }, [customFields]);

    const handleEditFieldName = useCallback((oldTitle: string, newTitle: string, create?: boolean) => {
        if (!create) {
            setCustomFields(prev => (prev.map(custom => custom.name.toLowerCase() === oldTitle.toLowerCase()
                ? ({ ...custom, name: newTitle.trim() }) : custom) as CustomFieldType[])
                    .map(({ name, additionalContextQuery }) => ({name, additionalContextQuery})));
            setCustomField(prev => ({ ...prev, name: newTitle.trim() } as CustomFieldType));

            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let hasRenamedCustomField = false;

                const updatedDashboard = { ...dashboard,
                    customFields: dashboard.customFields?.map(custom => {
                        if (custom.name.toLowerCase() === oldTitle.toLowerCase()) {
                            hasRenamedCustomField = true;
                            return ({ ...custom, name: newTitle.trim() });
                        }

                        return custom;
                    }) ?? [] };

                if (hasRenamedCustomField)
                    updatedDashboards.push(updatedDashboard);
            });

            if (!!updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        } else {
           handleUpdateFieldValue(newTitle.trim(), '');
        }
    }, [setCustomFields, dashboards, setDashboards, handleUpdateFieldValue]);

    const handleUpdateContext = useCallback(() => {
        handleUpdateFieldValue(customField!.name, customField!.additionalContextQuery || '');
        onClose();
    }, [customField, handleUpdateFieldValue, onClose]);

    const handleDeleteField = useCallback((title: string) => {
        setCustomFields(prev => prev.filter(custom => custom.name.toLowerCase() !== title.toLowerCase())
            .map(({ name, additionalContextQuery }) => ({ name, additionalContextQuery })));
        onClose();
    }, [onClose, setCustomFields]);

    useEffect(() => {
        if (settingsLoaded) {
            if (!!filterType) {
                if (!!customFields.length)
                    setCustomField(prev => customFields.find(custom => custom?.name === filterType) ?? prev);
            } else {
                handleUpdateFieldValue('', '', true);
            }
        }
    // eslint-disable-next-line
    }, [settingsLoaded]);

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <Sentence className={classes.headerIcon} />
                <span>{'Custom field'}</span>
            </DialogTitle>
            <Typography color={theme.colors.neutral['600']} pt={1}>
                {'Text fields extract short-form responses from ingested data'}
            </Typography>
            <Divider sx={{ margin: '8px 0', width: '100%' }} />
            <DialogContent className={classes.dialogContent}>
                {!!customField && (<>
                    <CustomField
                        fieldName={customField.name}
                        fieldContext={customField.additionalContextQuery}
                        onContext={(newContext) => setCustomField(prev => ({ ...prev, additionalContextQuery: newContext } as CustomFieldType))}
                        onEditTitle={(newTitle) => handleEditFieldName(customField.name, newTitle, customField.name === '')}
                        onDelete={() => handleDeleteField(customField.name)}
                        create={customField.name === ''} />
                </>)}
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Button className={classes.cancelBtn} onClick={onClose}> {'Back'} </Button>
                <Button variant="contained" className={classes.saveBtn}
                    onClick={handleUpdateContext}
                    disabled={!customField?.additionalContextQuery}> {'Save'}  </Button>
            </DialogActions>
        </Dialog>
    </>);
}

export default ManageCustomFieldsModal;